/**
 * Render `source` turned `turns` quarter-turns clockwise. Always drawn from the
 * original file so repeated rotations don't stack JPEG re-encoding loss.
 * Decoding honours the EXIF orientation flag, so a phone photo that only looks
 * sideways because of EXIF comes out upright after one turn like any other
 * image. PNGs stay PNG (lossless, keeps alpha); anything else becomes JPEG on
 * white, since JPEG has no alpha and transparent areas would turn black.
 */
export async function rotateImage(
  source: Blob,
  name: string,
  turns: number
): Promise<File> {
  turns = ((turns % 4) + 4) % 4;
  if (turns === 0) {
    return new File([source], name, { type: source.type });
  }
  const bitmap = await createImageBitmap(source, {
    imageOrientation: 'from-image',
  });
  const swap = turns % 2 === 1;
  const canvas = document.createElement('canvas');
  canvas.width = swap ? bitmap.height : bitmap.width;
  canvas.height = swap ? bitmap.width : bitmap.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas 2d context unavailable');
  }
  const png = source.type === 'image/png';
  if (!png) {
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((turns * Math.PI) / 2);
  ctx.drawImage(bitmap, -bitmap.width / 2, -bitmap.height / 2);
  bitmap.close();

  const type = png ? 'image/png' : 'image/jpeg';
  const rotated = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, type, 0.92)
  );
  if (!rotated) {
    throw new Error('Failed to encode rotated image');
  }
  const ext = png ? '.png' : '.jpg';
  return new File([rotated], name.replace(/\.[^.]+$/, '') + ext, { type });
}
